import React, { useMemo } from "react"
import { Box, useTheme, Typography, Button, Avatar } from "@mui/material"
import { deepmerge } from "@mui/utils"

import { getIcon } from "utils/icon"
import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"

import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"

import { OrdinalSelector } from "components/OrdinalSelector"
import { PageCard } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import {
  ArrowLeftRounded,
  ArrowRightRounded,
  OriginMarkerIcon,
  PauseIcon,
  PlayArrowIcon,
  RefreshArrowIcon,
  ShopIcon,
} from "components/icons"
import { VerticalStepProgressBar } from "components/Progress/VerticalStepProgressBar"
import { MotionDiv } from "components/motions"
import { SkeletonStepByStepSidebar } from "components/domains/direction/kiosk-h"
import { useStepTracking } from "hooks/analytics"

export const StepByStepKioskHPage = ({ Layout = Box }) => {
  const { t } = useTranslation()

  const { dataLoaded, isMultiOrdinalVenue } = useVenue()
  const {
    isReady,
    currentOrdinal,
    currentLevel,
    currentStepNumber,
    originName,
    destinationName,
    steps,
    route,
    originLogo,
    originCategoryIconPath,
    destinationLogo,
    destinationCategoryIconPath,
    // Auto play
    hasNextStep,
    hasPrevStep,
    isPlaying,
    goToStepByIndex,
    goToNextStep,
    goToPrevStep,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
  } = useStepByStep()

  useStepTracking({
    currentStep: currentStepNumber,
    steps,
    enabled: isReady,
  })

  const stepsAvailable = dataLoaded && steps && steps.length > 0

  useStepByStepMap(route, currentStepNumber, {
    offset: {
      bottom: 0,
    },
    enabled: stepsAvailable,
  })

  const theme = useTheme()

  const prevIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.prevIcon) || <ArrowLeftRounded />,
    [theme]
  )
  const nextIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.nextIcon) || <ArrowRightRounded />,
    [theme]
  )

  const playIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.playIcon) || <PlayArrowIcon />,
    [theme]
  )
  const playAgainIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.playAgainIcon) || <RefreshArrowIcon />,
    [theme]
  )

  const pauseIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.pauseIcon) || <PauseIcon />,
    [theme]
  )

  const {
    root,
    stepContainer,
    stepItem,
    originIcon,
    originAvatar,
    ordinalSelector,
    actionContainer,
    playerActionContainer,
    secondaryButton,
    primaryButton,
    destinationIcon,
    locationAvatar,
    locationText,
    originText,
    stepProgressBarContainer,
    stepProgressBar,
    sidebarContent,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, footer, playerButton } = sideBarStyleOverrides

  const originIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <ShopIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  return (
    <Layout
      aside={
        <PageCard>
          <MotionDiv
            sx={deepmerge(wrapper, sidebarContent)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={body}>
              {!stepsAvailable ? (
                <SkeletonStepByStepSidebar />
              ) : (
                <Box sx={root}>
                  <Box sx={stepContainer}>
                    <Box sx={stepItem}>
                      <Avatar
                        src={destinationLogo}
                        sx={locationAvatar}
                        variant="logo"
                      >
                        {getIcon(
                          destinationCategoryIconPath,
                          destinationIcon
                        ) || destinationIconComp}
                      </Avatar>
                      <Typography variant="subtitle1" sx={locationText}>
                        {t(destinationName)}
                      </Typography>
                    </Box>
                    {/* kiosk-h progress bar */}
                    <Box sx={stepProgressBarContainer}>
                      <VerticalStepProgressBar
                        steps={steps}
                        currentStep={currentStepNumber}
                        sx={stepProgressBar}
                        handleClickStep={goToStepByIndex}
                        direction="btt"
                        enableAnimateScroll={true}
                      />
                    </Box>
                    {/* end of kiosk-h progress bar */}
                    <Box sx={stepItem}>
                      <Avatar
                        src={originLogo}
                        sx={deepmerge(locationAvatar, originAvatar)}
                        variant="logo"
                      >
                        {getIcon(originCategoryIconPath, originIcon) ||
                          originIconComp}
                      </Avatar>
                      <Typography
                        variant="subtitle1"
                        sx={deepmerge(locationText, originText)}
                      >
                        {t(originName)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={actionContainer}>
                    <Box sx={playerActionContainer}>
                      <Button
                        className="prevButton"
                        variant="outlined"
                        sx={deepmerge(playerButton, secondaryButton)}
                        startIcon={prevIcon}
                        onClick={goToPrevStep}
                        disabled={isPlaying || !hasPrevStep}
                      >
                        {t("Previous")}
                      </Button>
                      {isPlaying ? (
                        <Button
                          className="playButton"
                          variant="outlined"
                          sx={deepmerge(playerButton, primaryButton)}
                          startIcon={pauseIcon}
                          onClick={pauseAutoPlay}
                          disabled={!isPlaying}
                        >
                          {t("Stop")}
                        </Button>
                      ) : (
                        <Button
                          className="playButton"
                          variant="outlined"
                          sx={deepmerge(playerButton, primaryButton)}
                          startIcon={hasNextStep ? playIcon : playAgainIcon}
                          onClick={
                            hasNextStep ? resumeAutoPlay : restartAutoPlay
                          }
                        >
                          {hasNextStep ? t("Play") : t("Play Again")}
                        </Button>
                      )}
                      <Button
                        className="nextButton"
                        variant="contained"
                        sx={deepmerge(playerButton, secondaryButton)}
                        endIcon={nextIcon}
                        onClick={goToNextStep}
                        disabled={isPlaying || !hasNextStep}
                      >
                        {t("Next")}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={footer}>
              <NavigationControl SearchButtonProps={{ hidden: true }} />
            </Box>
          </MotionDiv>
        </PageCard>
      }
    >
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            label="Floor"
            levels={currentLevel}
            selectedOrdinal={currentOrdinal}
          />
        </Box>
      )}
    </Layout>
  )
}
